import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import MysticalParticles from './MysticalParticles';
import { Users, Plus, Edit, Eye, Trash2, Save, X, Upload } from 'lucide-react';

const VeilkinProfiles = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('mystveil_token'));
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [formData, setFormData] = useState({
    characterName: '',
    characterDescription: '',
    characterImage: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
        setFormData({...formData, characterImage: e.target.result});
      };
      reader.readAsDataURL(file);
    }
  };

  const resetForm = () => {
    setFormData({
      characterName: '',
      characterDescription: '',
      characterImage: ''
    });
    setImageFile(null);
    setImagePreview(null);
  };

  useEffect(() => {
    const loadProfiles = async () => {
      if (!token) {
        setError('Please log in to view your characters');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await fetch('https://api.4fingerstudios.com/api/mystveil/veilkin-profiles', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          if (response.status === 401) {
            setError('Session expired. Please log in again.');
            localStorage.removeItem('mystveil_token');
            setToken(null);
          } else {
            throw new Error(`Failed to load profiles: ${response.status}`);
          }
          return;
        }

        const data = await response.json();
        setProfiles(data || []);
      } catch (err) {
        setError('Failed to load character profiles');
        console.error('Profiles load error:', err);
      } finally {
        setLoading(false);
      }
    };

    loadProfiles();
  }, [token]);

  const handleCreateProfile = async (e) => {
    e.preventDefault();
    if (!formData.characterName.trim()) {
      alert('Character name is required');
      return;
    }

    try {
      const response = await fetch('https://api.4fingerstudios.com/api/mystveil/veilkin-profiles', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Failed to create character: ${response.status}`);
      }

      const newProfile = await response.json();
      setProfiles([...profiles, newProfile]);
      setShowCreateForm(false);
      setFormData({ characterName: '', characterDescription: '', characterImage: '' });
    } catch (err) {
      alert(`Failed to create character: ${err.message}`);
    }
  };

  const handleUpdateProfile = async (profileId) => {
    try {
      const response = await fetch(`https://api.4fingerstudios.com/api/mystveil/veilkin-profiles/${profileId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Failed to update character: ${response.status}`);
      }

      // Update the profile in the list
      setProfiles(profiles.map(p => 
        p.id === profileId 
          ? { ...p, ...formData }
          : p
      ));
      setEditingProfile(null);
      setFormData({ characterName: '', characterDescription: '', characterImage: '' });
    } catch (err) {
      alert(`Failed to update character: ${err.message}`);
    }
  };

  const startEdit = (profile) => {
    setEditingProfile(profile.id);
    setFormData({
      characterName: profile.characterName,
      characterDescription: profile.characterDescription,
      characterImage: profile.characterImage
    });
  };

  const cancelEdit = () => {
    setEditingProfile(null);
    setFormData({ characterName: '', characterDescription: '', characterImage: '' });
  };

  if (!token) {
    return (
      <div className="min-h-screen flex flex-col relative">
        <div className="fixed inset-0 -z-10">
          <img 
            src="https://cdn.4fingerstudios.com/Mystveil/Mystveil_map.png" 
            alt="Mystveil Township Map" 
            className="w-full h-full object-cover opacity-30 blur-sm" 
          />
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-pink-900/20 to-purple-900/20"></div>
        </div>
        <MysticalParticles />
        <Navbar />
        
        <main className="flex-grow flex items-center justify-center px-4 py-8">
          <div className="mystical-card max-w-md mx-auto text-center">
            <h1 className="mystical-title mb-6">Veilkin Characters</h1>
            <p className="mystical-text mb-6">Please log in to view your characters</p>
            <Link to="/auth/login" className="mystical-button">
              Login
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col relative">
        <div className="fixed inset-0 -z-10">
          <img 
            src="https://cdn.4fingerstudios.com/Mystveil/Mystveil_map.png" 
            alt="Mystveil Township Map" 
            className="w-full h-full object-cover opacity-30 blur-sm" 
          />
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-pink-900/20 to-purple-900/20"></div>
        </div>
        <MysticalParticles />
        <Navbar />
        
        <main className="flex-grow flex items-center justify-center px-4 py-8">
          <div className="mystical-card">
            <p className="mystical-text">Loading your characters...</p>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col relative">
        <div className="fixed inset-0 -z-10">
          <img 
            src="https://cdn.4fingerstudios.com/Mystveil/Mystveil_map.png" 
            alt="Mystveil Township Map" 
            className="w-full h-full object-cover opacity-30 blur-sm" 
          />
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-pink-900/20 to-purple-900/20"></div>
        </div>
        <MysticalParticles />
        <Navbar />
        
        <main className="flex-grow flex items-center justify-center px-4 py-8">
          <div className="mystical-card max-w-md mx-auto text-center">
            <h1 className="mystical-title mb-6">Error</h1>
            <p className="mystical-text mb-6 text-red-600">{error}</p>
            <Link to="/" className="mystical-button">
              Return Home
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col relative">
      {/* Magical Background */}
      <div className="fixed inset-0 -z-10">
        <img 
          src="https://cdn.4fingerstudios.com/Mystveil/Mystveil_map.png" 
          alt="Mystveil Township Map" 
          className="w-full h-full object-cover opacity-30 blur-sm" 
        />
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-pink-900/20 to-purple-900/20"></div>
      </div>

      {/* Magical Particles */}
      <MysticalParticles />

      {/* Navigation */}
      <Navbar />

      {/* Main Content */}
      <main className="flex-grow px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="mystical-card mb-8">
            <div className="flex items-center justify-between mb-6">
              <h1 className="mystical-title text-black">
                Veilkin Characters
              </h1>
              {profiles.length < 2 && (
                <button 
                  onClick={() => setShowCreateForm(true)}
                  className="mystical-button"
                >
                  <Plus className="mr-2" size={20} />
                  Create Character
                </button>
              )}
            </div>
            
            <div className="mystical-card bg-white bg-opacity-95 mb-6">
              <p className="mystical-text text-black">
                Manage your Veilkin characters. You can create up to 2 characters during the beta period.
                Each character can be customized with upgrades and artwork.
              </p>
            </div>

            {/* Character Slots Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div className="mystical-card bg-white bg-opacity-95">
                <h3 className="mystical-subtitle text-black mb-2">Character Slots</h3>
                <p className="mystical-text text-gray-700">
                  Used: {profiles.length} / 2 (Beta Limit)
                </p>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                  <div 
                    className="bg-purple-800 h-2 rounded-full" 
                    style={{ width: `${(profiles.length / 2) * 100}%` }}
                  ></div>
                </div>
              </div>
              <div className="mystical-card bg-white bg-opacity-95">
                <h3 className="mystical-subtitle text-black mb-2">Quick Stats</h3>
                <p className="mystical-text text-gray-700">
                  Total Characters: {profiles.length}
                </p>
                <p className="mystical-text text-gray-700">
                  Available Slots: {2 - profiles.length}
                </p>
              </div>
            </div>
          </div>

          {/* Create Character Form */}
          {showCreateForm && (
            <div className="mystical-card mb-8">
              <h3 className="mystical-subtitle text-black text-xl mb-6">
                Create New Character
              </h3>
              <form onSubmit={handleCreateProfile} className="space-y-4">
                <div>
                  <label className="block mystical-text text-black font-bold mb-2">
                    Character Name *
                  </label>
                  <input
                    type="text"
                    value={formData.characterName}
                    onChange={(e) => setFormData({...formData, characterName: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-700 focus:border-transparent"
                    placeholder="Enter your character's name"
                    required
                  />
                </div>
                <div>
                  <label className="block mystical-text text-black font-bold mb-2">
                    Character Description (Optional)
                  </label>
                  <textarea
                    value={formData.characterDescription}
                    onChange={(e) => setFormData({...formData, characterDescription: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-700 focus:border-transparent"
                    rows="3"
                    placeholder="Describe your character's personality, backstory, or appearance (optional)"
                  />
                </div>
                <div>
                  <label className="block mystical-text text-black font-bold mb-2">
                    Completed MYO Character Sheet *
                  </label>
                  <div className="space-y-3">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-700 focus:border-transparent"
                      required
                    />
                    {imagePreview && (
                      <div className="mt-3">
                        <img 
                          src={imagePreview} 
                          alt="Character preview" 
                          className="w-32 h-32 object-cover rounded-lg border-2 border-purple-300"
                        />
                        <p className="text-sm text-gray-600 mt-1">Preview of uploaded character sheet</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex space-x-4">
                  <button type="submit" className="mystical-button">
                    <Save className="mr-2" size={20} />
                    Create Character
                  </button>
                  <button 
                    type="button" 
                    onClick={() => {
                      setShowCreateForm(false);
                      resetForm();
                    }}
                    className="mystical-button bg-gray-600 hover:bg-gray-700"
                  >
                    <X className="mr-2" size={20} />
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Character List */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {profiles.map(profile => (
              <div key={profile.id} className="mystical-card">
                <div className="flex items-start space-x-4">
                  {profile.characterImage ? (
                    <img 
                      src={profile.characterImage} 
                      alt={profile.characterName}
                      className="w-20 h-20 rounded-md object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-20 h-20 bg-gray-200 rounded-md flex items-center justify-center flex-shrink-0">
                      <Users className="text-gray-400" size={32} />
                    </div>
                  )}
                  
                  <div className="flex-1">
                    {editingProfile === profile.id ? (
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={formData.characterName}
                          onChange={(e) => setFormData({...formData, characterName: e.target.value})}
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-700"
                          placeholder="Character name"
                        />
                        <textarea
                          value={formData.characterDescription}
                          onChange={(e) => setFormData({...formData, characterDescription: e.target.value})}
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-700"
                          rows="2"
                          placeholder="Character description"
                        />
                        <input
                          type="url"
                          value={formData.characterImage}
                          onChange={(e) => setFormData({...formData, characterImage: e.target.value})}
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-700"
                          placeholder="Image URL"
                        />
                        <div className="flex space-x-2">
                          <button 
                            onClick={() => handleUpdateProfile(profile.id)}
                            className="mystical-button"
                          >
                            <Save className="mr-1" size={16} />
                            Save
                          </button>
                          <button 
                            onClick={cancelEdit}
                            className="mystical-button bg-gray-600 hover:bg-gray-700"
                          >
                            <X className="mr-1" size={16} />
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <h3 className="mystical-subtitle text-black text-lg mb-2">
                          {profile.characterName}
                        </h3>
                        <p className="mystical-text text-gray-700 mb-4">
                          {profile.characterDescription || 'No description provided'}
                        </p>
                        <div className="flex space-x-2">
                          <button 
                            onClick={() => startEdit(profile)}
                            className="mystical-button"
                          >
                            <Edit className="mr-1" size={16} />
                            Edit
                          </button>
                          <Link 
                            to={`/upgrades/${profile.id}`}
                            className="mystical-button"
                          >
                            <Eye className="mr-1" size={16} />
                            View Upgrades
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Empty State */}
          {profiles.length === 0 && (
            <div className="mystical-card text-center">
              <Users className="mx-auto text-gray-400 mb-4" size={64} />
              <h3 className="mystical-subtitle text-black text-xl mb-4">
                No Characters Yet
              </h3>
              <p className="mystical-text text-gray-600 mb-6">
                Create your first Veilkin character to begin your mystical journey!
              </p>
              <button 
                onClick={() => setShowCreateForm(true)}
                className="mystical-button"
              >
                <Plus className="mr-2" size={20} />
                Create Your First Character
              </button>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <Link to="/profile" className="mystical-button">
              Back to Profile
            </Link>
            <Link to="/" className="mystical-button">
              Return Home
            </Link>
            <Link to="/guide/MYO" className="mystical-button">
              MYO Guide
            </Link>
          </div>
        </div>
      </main>

      {/* Mystical Footer */}
      <footer className="mystical-nav py-6 mt-auto">
        <div className="container mx-auto px-4 text-center">
          <p className="mystical-text font-bold">
            © 2025 4fingerstudios <br /> 
            © 2024 Reckless Dreams. All Rights Reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default VeilkinProfiles;
